import type { Level } from '@/features/journeys'
import type { Mission } from '@/features/missions/types/mission'
import type { Cosmetic } from '@/features/profiles/types/cosmetic'
import type { Media } from '@/shared/types/media'
import type { PublicUser } from '@/shared/types/user'

export type FeedItemProgressResource = {
  id: number
  user_id: number
  resource_type: string
  resource_id: number
  resource: Level | Mission | unknown
  created_at?: string
  updated_at?: string
}

export type FeedItemFollowResource = {
  id: number
  created_at: string
  updated_at: string
}

export type FeedItemNotificationResource = {
  id: number
  user_id: number
  name: string
  subject: string
  subtitle: string
  content: string
  image?: Media | null
  link_url?: string | null
  link_title?: string | null
  email_delivery?: boolean
  delivery_at?: string | null
  status?: string
  kind?: string
  created_at?: string
  delivered_at?: string | null
}

export type FeedItemResource =
  | FeedItemProgressResource
  | FeedItemFollowResource
  | FeedItemNotificationResource

// Contrato mínimo de terceiros (LGPD/ECA): a API não devolve mais o `id`
// interno — a prévia é identificada por `uid`.
export type FeedItemLikePreviewUser = {
  uid: string
  name: string
  is_following?: boolean
  avatar?: Cosmetic | null
  frame?: Cosmetic | null
  background?: Cosmetic | null
}

export type FeedItemLikes = {
  count: number
  liked_by_me: boolean
  preview_users: FeedItemLikePreviewUser[]
}

export type FeedItem = {
  id: number
  created_at: string
  resource: FeedItemResource
  from: PublicUser
  likes?: FeedItemLikes
}

export function isNotificationFeedItem(
  item: FeedItem
): item is FeedItem & { resource: FeedItemNotificationResource } {
  return (
    typeof item.resource === 'object' &&
    item.resource !== null &&
    'subject' in item.resource &&
    'subtitle' in item.resource &&
    'content' in item.resource
  )
}

export function isProgressFeedItem(
  item: FeedItem
): item is FeedItem & { resource: FeedItemProgressResource } {
  return (
    typeof item.resource === 'object' &&
    item.resource !== null &&
    'resource_type' in item.resource &&
    'resource' in item.resource
  )
}

export function isFollowFeedItem(
  item: FeedItem
): item is FeedItem & { resource: FeedItemFollowResource } {
  return (
    typeof item.resource === 'object' &&
    item.resource !== null &&
    !('resource_type' in item.resource) &&
    !('subject' in item.resource) &&
    'created_at' in item.resource &&
    'updated_at' in item.resource
  )
}

export function getFeedItemKey(item: FeedItem) {
  if (isNotificationFeedItem(item)) return `notification:${item.id}`
  if (isProgressFeedItem(item)) return `progress:${item.id}`
  if (isFollowFeedItem(item)) return `follow:${item.id}`
  return `feed:${item.id}`
}

export function getFeedItemReactable(item: FeedItem): {
  reactable_type: string
  reactable_id: number
} {
  if (isProgressFeedItem(item)) {
    return { reactable_type: 'ProgressResource', reactable_id: item.resource.id }
  }
  if (isFollowFeedItem(item)) {
    return { reactable_type: 'Follow', reactable_id: item.resource.id }
  }
  if (isNotificationFeedItem(item)) {
    return { reactable_type: 'NotificationMessage', reactable_id: item.resource.id }
  }
  throw new Error('Unsupported feed item for reactions')
}
